"use client";

import { useState, useEffect, useRef } from "react";

const faqs = [
  {
    category: "Product",
    questions: [
      {
        q: "What is kratom powder?",
        a: "Kratom (Mitragyna speciosa) is a botanical product derived from a tree native to Southeast Asia. Kratapillar sells it as a fine-milled powder for adult consumers. We make no medical claims about its effects.",
      },
      {
        q: "What's the difference between Red, White, and Green line?",
        a: "The three lines correspond to different vein colors of the kratom leaf, which are processed differently. Red Line (Grounded) comes from red-vein leaves, White Line (Focused) from white-vein, and Green Line (Balanced) from green-vein. Each has a distinct character — experienced users typically have a preference.",
      },
      {
        q: "How much powder is in one kilo?",
        a: "Each order contains 1,000 grams (1 kilogram) of fine-milled kratom powder, packaged in a resealable kraft bag with a zip-lock seal to maintain freshness.",
      },
      {
        q: "What free sample is included?",
        a: "Every kilo order includes a complimentary 50-gram sample of one of the other two lines (we rotate based on inventory). It's our way of letting you explore the full range without committing to a second kilo.",
      },
      {
        q: "Is this product legal?",
        a: "Kratom's legal status varies by location. It is legal at the federal level in the United States, but is banned or restricted in certain states and municipalities. It is your responsibility to verify the legal status in your jurisdiction before ordering.",
      },
    ],
  },
  {
    category: "Lab Testing",
    questions: [
      {
        q: "Who tests your products?",
        a: "We use Cascadia Labs in Portland, OR — an ISO/IEC 17025:2017 accredited independent laboratory with no business relationship to Kratapillar beyond testing services.",
      },
      {
        q: "What does each COA test for?",
        a: "Each Certificate of Analysis covers: total alkaloid content (Mitragynine), microbial contaminants (Salmonella, E. coli, total plate count), heavy metals (arsenic, cadmium, lead, mercury), mold and yeast, and pesticide residues.",
      },
      {
        q: "How do I know the COA matches my batch?",
        a: "Each product bag is printed with a batch ID that corresponds directly to a COA on our Lab Testing page. You can look up your exact batch at any time.",
      },
      {
        q: "What happens if a batch fails testing?",
        a: "Simple: it doesn't ship. We reject any batch that does not meet our internal standards and the lab's pass criteria. We do not publish failed COAs, but we also do not sell failed batches under any circumstances.",
      },
    ],
  },
  {
    category: "Orders & Shipping",
    questions: [
      {
        q: "How fast do orders ship?",
        a: "Orders are processed and shipped within 24 business hours. Standard shipping typically takes 3–5 business days. Expedited options are available at checkout.",
      },
      {
        q: "Do you ship discreetly?",
        a: "Yes. All orders ship in plain, unmarked packaging. The return address does not reference Kratapillar or kratom.",
      },
      {
        q: "Do you ship internationally?",
        a: "Currently, we only ship within the United States. International shipping is on our roadmap.",
      },
      {
        q: "Can I track my order?",
        a: "Yes. You\'ll receive a tracking number via email as soon as your order ships.",
      },
    ],
  },
  {
    category: "Guarantee & Returns",
    questions: [
      {
        q: "How does the 30-day guarantee work?",
        a: "If you're not satisfied with your order for any reason within 30 days of delivery, contact us at support@kratapillar.com. We'll issue a full refund — no return required, no questions asked.",
      },
      {
        q: "Do I need to return the product to get a refund?",
        a: "No. We do not require you to return the product. Simply contact us within 30 days of delivery and we process your refund.",
      },
      {
        q: "What if my order arrives damaged?",
        a: "Contact us within 72 hours of delivery with a photo of the damage. We\'ll ship a replacement immediately at no charge.",
      },
    ],
  },
];

export default function FAQInteractive() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.05 }
    );
    refs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const toggle = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="py-16" style={{ backgroundColor: "#F6F5F2" }}>
      <div className="max-w-container mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {faqs.map((section, si) => (
            <div
              key={section.category}
              ref={(el) => { refs.current[si] = el; }}
              className={`reveal stagger-${si + 1} mb-14`}
            >
              {/* Category label */}
              <div className="flex items-center gap-4 mb-6">
                <span className="section-label">{section.category}</span>
                <div className="flex-1 h-px" style={{ backgroundColor: "#E0DFDB" }} />
              </div>

              {/* Questions */}
              <div className="space-y-3">
                {section.questions.map((item, qi) => {
                  const key = `${si}-${qi}`;
                  const isOpen = !!openItems[key];
                  return (
                    <div
                      key={qi}
                      style={{
                        backgroundColor: "white",
                        border: `1px solid ${isOpen ? "#3F5A45" : "#E0DFDB"}`,
                        transition: "border-color 0.2s ease",
                      }}
                    >
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between gap-4 p-5 text-left"
                        aria-expanded={isOpen}
                      >
                        <span
                          className="text-sm font-semibold"
                          style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                        >
                          {item.q}
                        </span>
                        <div
                          className="w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300"
                          style={{
                            transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                            color: isOpen ? "#3F5A45" : "#6B6B6B",
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </div>
                      </button>
                      <div
                        className="accordion-content"
                        style={{ maxHeight: isOpen ? "400px" : "0px" }}
                      >
                        <div
                          className="px-5 pb-5 text-sm leading-relaxed"
                          style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                        >
                          {item.a}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Still have questions */}
          <div
            ref={(el) => { refs.current[faqs.length] = el; }}
            className="reveal p-8 text-center"
            style={{ backgroundColor: "#1F1F1F" }}
          >
            <h3
              className="font-serif font-bold text-white mb-2"
              style={{
                fontFamily: "Fraunces, Georgia, serif",
                fontSize: "1.5rem",
                letterSpacing: "-0.02em",
              }}
            >
              Still have a question?
            </h3>
            <p
              className="text-sm mb-6"
              style={{ color: "rgba(255,255,255,0.55)", fontFamily: "Inter, system-ui, sans-serif" }}
            >
              We typically respond within a few hours.
            </p>
            <a
              href="mailto:support@kratapillar.com"
              className="btn-primary text-sm"
              style={{ backgroundColor: "#3F5A45" }}
            >
              Email Us
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}